import { Link } from "@tanstack/react-router";
import SiteCoreLinks from "@/components/SiteCoreLinks";
import { LeadFormModal } from "@/components/LeadFormModal";
import { SERVICES } from "@/data/services";

const PHONE = "083-249-4999";

// Group services by category for the matrix
const GROUPS = [
  {
    title: "📜 แปลเอกสาร · รับรอง · Notary",
    ids: [
      "translation",
      "sworn-translation",
      "notary-public",
      "naati",
      "mfa-legalization",
      "embassy-legalization",
      "apostille",
      "interpreter",
    ],
  },
  {
    title: "🛂 วีซ่า · ตม. · ใบอนุญาตทำงาน",
    ids: [
      "visa-thai-citizens",
      "visa-foreigners-thailand",
      "tm30-90day",
      "work-permit-boi",
      "ltr-elite-visa",
      "apec-abtc",
      "permanent-residence",
      "thai-citizenship",
    ],
  },
  {
    title: "⚖️ กฎหมาย · ครอบครัว · ทรัพย์สิน",
    ids: [
      "marriage-thai-foreign",
      "civil-family-probate",
      "real-estate-conveyancing",
      "criminal-defense",
      "police-clearance",
    ],
  },
  {
    title: "🏢 ธุรกิจ · ภาษี · ทรัพย์สินทางปัญญา",
    ids: [
      "company-boi",
      "trademark-ip",
      "tax-law",
      "labor-employment",
      "banking-finance",
      "pdpa-cybersecurity",
      "ma-private-equity",
      "international-arbitration",
    ],
  },
] as const;

const fmt = (n: number) => "฿" + n.toLocaleString("en-US");

export function PricingPage() {
  return (
    <main className="min-h-screen bg-gradient-to-b from-[#fafaf7] to-white">
      {/* Hero */}
      <section className="border-b border-amber-500/20 bg-navy-deep text-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20">
          <nav className="mb-4 text-xs text-white/80" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-amber-300">
              หน้าแรก
            </Link>
            <span className="mx-2">›</span>
            <span className="text-amber-300">ราคาบริการ</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-300">
            💰 Transparent Pricing · ไม่มีค่าใช้จ่ายแฝง
          </div>
          <h1 className="mt-4 text-3xl font-extrabold leading-tight sm:text-5xl">
            ราคาบริการครบทุกหมวด
            <br />
            <span className="text-amber-400">โปร่งใส · ตรวจสอบได้ · ก่อนเริ่มงาน</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-white/80 sm:text-lg">
            ราคาด้านล่างเป็นช่วงราคามาตรฐานปี 2568 — ราคาสุดท้ายขึ้นกับจำนวนหน้า ภาษา ความเร่งด่วน
            และจำนวนเอกสารที่ต้องการรับรอง{" "}
            <strong className="text-white">ส่งเอกสารให้เราประเมินฟรีภายใน 15 นาที</strong>
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={`tel:+66832494999`}
              className="rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-navy-deep transition hover:bg-amber-400"
            >
              📞 โทรขอใบเสนอราคา {PHONE}
            </a>
            <LeadFormModal
              trigger={
                <button
                  type="button"
                  className="rounded-full border border-white/30 bg-white/5 px-6 py-3 text-sm font-bold text-white transition hover:bg-white/10"
                >
                  ✍️ ส่งเอกสารประเมินออนไลน์
                </button>
              }
            />
            <a
              href="https://line.me/ti/p/@nycli"
              target="_blank"
              rel="noreferrer"
              className="rounded-full border border-[#06C755]/40 bg-[#04A03A]/10 px-6 py-3 text-sm font-bold text-[#06C755] transition hover:bg-[#04A03A]/20"
            >
              💬 LINE @nycli
            </a>
          </div>
        </div>
      </section>

      {/* Quick highlight cards */}
      <section className="border-b border-black/5 bg-white">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 py-10 sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
          {[
            { v: "฿500", l: "แปลไทย-อังกฤษ /หน้า", sub: "รวมตราประทับบริษัท" },
            { v: "฿1,500", l: "Notary Public", sub: "ทนายขึ้นทะเบียนสภาทนายความ" },
            { v: "฿200", l: "ค่ารับรอง MFA /ใบ", sub: "+ บริการนำไปยื่น ฿2,500" },
            { v: "฿15 นาที", l: "ตอบใบเสนอราคา", sub: "ในเวลาทำการ จันทร์–เสาร์" },
          ].map((c) => (
            <div
              key={c.l}
              className="rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-50 to-white p-5 shadow-sm"
            >
              <div className="text-3xl font-extrabold text-navy-deep">{c.v}</div>
              <div className="mt-1 text-sm font-bold text-navy-deep/80">{c.l}</div>
              <div className="mt-0.5 text-xs text-navy-deep/60">{c.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing matrix by category */}
      <section className="mx-auto max-w-7xl space-y-12 px-4 py-14 sm:px-6">
        {GROUPS.map((g) => (
          <div key={g.title}>
            <h2 className="mb-6 text-2xl font-extrabold text-navy-deep sm:text-3xl">{g.title}</h2>
            <div className="grid gap-5 md:grid-cols-2">
              {g.ids.map((id) => {
                const s = SERVICES[id as keyof typeof SERVICES];
                if (!s) return null;
                return (
                  <article
                    key={s.id}
                    className="rounded-2xl border border-black/8 bg-white p-6 shadow-sm transition hover:border-amber-500/30 hover:shadow-md"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base font-bold text-navy-deep sm:text-lg">
                          {s.nameTh}
                        </h3>
                        <p className="mt-0.5 text-xs text-navy-deep/60">{s.nameEn}</p>
                      </div>
                      <div className="flex-none text-right">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-600">
                          เริ่มต้น
                        </div>
                        <div className="text-xl font-extrabold text-navy-deep">
                          {fmt(s.priceFrom)}
                        </div>
                      </div>
                    </div>
                    <ul className="mt-4 space-y-1.5 border-t border-dashed border-black/10 pt-3 text-xs text-navy-deep/80">
                      {s.prices.slice(0, 4).map((p, i) => (
                        <li key={i} className="flex items-baseline justify-between gap-3">
                          <span className="min-w-0 truncate">{p.item}</span>
                          <span className="flex-none font-bold text-navy-deep">{p.price}</span>
                        </li>
                      ))}
                      {s.prices.length > 4 && (
                        <li className="text-[11px] italic text-navy-deep/50">
                          + อีก {s.prices.length - 4} รายการ ดูในหน้าบริการ
                        </li>
                      )}
                    </ul>
                    <div className="mt-4 flex items-center justify-between gap-2">
                      <div className="text-[11px] text-navy-deep/60">⏱ {s.turnaround}</div>
                      <Link
                        to="/services/$service"
                        params={{ service: s.slug }}
                        className="text-xs font-bold text-amber-700 hover:text-amber-900 hover:underline"
                      >
                        ดูรายละเอียด →
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* Pricing policies */}
      <section className="border-y border-amber-500/20 bg-amber-50/40">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <h2 className="text-2xl font-extrabold text-navy-deep sm:text-3xl">
            นโยบายราคา · เงื่อนไขที่ลูกค้าควรรู้
          </h2>
          <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "VAT 7%", d: "ราคาที่แสดงยังไม่รวม VAT — บวก 7% เมื่อขอใบกำกับภาษีเต็มรูป" },
              { t: "มัดจำ 50%", d: "งานทั่วไป มัดจำ 50% · Rush 4 ชม. ชำระ 100% ล่วงหน้า" },
              { t: "ส่วนลดปริมาณ", d: "งานเกิน 50 หน้า ลด 10–15% · Retainer รายปี ลดสูงสุด 25%" },
              {
                t: "รับประกันคุณภาพ",
                d: "หากถูกปฏิเสธจากราชการเพราะคำแปลของเรา แก้ไขฟรี + คืนค่าธรรมเนียม",
              },
              {
                t: "ใบกำกับภาษี",
                d: "ออกใบกำกับภาษีเต็มรูปได้ทันที · รับ Withholding Tax 3% สำหรับนิติบุคคล",
              },
              {
                t: "ชำระเงิน",
                d: "โอนแบงก์ · PromptPay · บัตรเครดิต · Stripe (ต่างประเทศ) · USDT",
              },
            ].map((p) => (
              <div key={p.t} className="rounded-xl bg-white p-5 ring-1 ring-amber-500/15">
                <div className="text-xs font-bold uppercase tracking-wider text-amber-700">
                  {p.t}
                </div>
                <div className="mt-1.5 text-sm text-navy-deep/80">{p.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
        <h2 className="text-2xl font-extrabold text-navy-deep sm:text-3xl">
          คำถามที่พบบ่อยเรื่องราคา
        </h2>
        <div className="mt-6 divide-y divide-black/10 rounded-2xl border border-black/10 bg-white">
          {[
            {
              q: "ราคาที่แสดงรวม VAT แล้วหรือยัง?",
              a: "ราคายังไม่รวม VAT 7% — กรณีต้องการใบกำกับภาษีเต็มรูปแบบ เพิ่ม VAT 7% จากยอดสุทธิ ลูกค้าทั่วไปที่ไม่ขอใบกำกับภาษีไม่ต้องเสีย VAT",
            },
            {
              q: "มีค่ามัดจำเท่าไหร่?",
              a: "งานทั่วไปมัดจำ 50% · งานเร่งด่วน (Rush 4 ชม.) ชำระ 100% ล่วงหน้า · งานองค์กรที่มี PO/สัญญารายปี ชำระ Net 30 หลังออกใบกำกับภาษี",
            },
            {
              q: "ส่วนลดสำหรับลูกค้าองค์กร / งานปริมาณมาก?",
              a: "งานเกิน 50 หน้า ลด 10–15% · สัญญา Retainer รายเดือน/รายปี ลดสูงสุด 25% · สำนักงานกฎหมาย/บริษัทแปล ราคา Wholesale ติดต่อทีม Account Manager",
            },
            {
              q: "รับชำระเงินช่องทางใดบ้าง?",
              a: "โอนธนาคารทุกแบงก์ไทย · PromptPay (Bank of Thailand QR Standard) · บัตรเครดิต Visa/Master/JCB/Amex ผ่าน 2C2P (PCI-DSS Level 1) · Stripe สำหรับลูกค้าต่างประเทศ 135+ สกุลเงิน · USDT (TRC20/ERC20) สำหรับ Crypto Business — ออกใบกำกับภาษี e-Tax Invoice ตามประกาศอธิบดี ฉบับ 31/2560 และ ป.รัษฎากร ม.86/4 ทุกการชำระ พร้อม TIN 0105556023896",
            },
            {
              q: "มีรับประกันคุณภาพและคืนเงินหรือไม่?",
              a: "รับประกัน 100% — หากเอกสารถูกปฏิเสธจากหน่วยงานราชการเพราะคำแปลของเรา เราแก้ไขให้ฟรีและคืนเงินค่าธรรมเนียมที่เสียไป",
            },
            {
              q: "ทำไมราคาบางที่ถูกกว่ามาก?",
              a: "ราคาถูกพิเศษมักเป็นนักแปลฟรีแลนซ์ที่ไม่ได้ขึ้นทะเบียนกรมการกงสุล — เอกสารจะถูกปฏิเสธจากสถานทูต/MFA ทำให้ต้องแปลใหม่ เสียทั้งเงินและเวลา · NYC Legal เป็นบริษัทขึ้นทะเบียนถูกต้องตามกฎหมาย เลข 0435567000061",
            },
          ].map((f) => (
            <details key={f.q} className="group p-5">
              <summary className="cursor-pointer list-none text-sm font-bold text-navy-deep marker:hidden">
                <span className="mr-2 text-amber-600 group-open:rotate-90 inline-block transition">
                  ▶
                </span>
                {f.q}
              </summary>
              <p className="mt-3 pl-6 text-sm leading-relaxed text-navy-deep/75">{f.a}</p>
            </details>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-navy-deep to-[#0a1a3d] p-8 text-center text-white">
          <h3 className="text-xl font-extrabold sm:text-2xl">ต้องการใบเสนอราคาที่แม่นยำ?</h3>
          <p className="mt-2 text-sm text-white/75">
            ส่งเอกสารให้เราประเมินฟรี — ทีมตอบใน 15 นาที (จันทร์–เสาร์ 09:00–18:00)
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <LeadFormModal
              trigger={
                <button
                  type="button"
                  className="rounded-full bg-amber-500 px-6 py-3 text-sm font-extrabold text-navy-deep transition hover:bg-amber-400"
                >
                  ✍️ ขอใบเสนอราคาฟรี
                </button>
              }
            />
            <Link
              to="/compare"
              className="rounded-full border border-white/30 bg-white/5 px-6 py-3 text-sm font-bold text-white transition hover:bg-white/10"
            >
              เปรียบเทียบกับเจ้าอื่น →
            </Link>
          </div>
        </div>
      </section>
      <SiteCoreLinks />
    </main>
  );
}
